"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "~/trpc/react";
import GreeterUI from "~/components/ui/greeter";
import SearchBoxUI from "~/components/ui/searchbox";
import SortFilterUI, { type FilterBy, type SortBy } from "~/components/_pages/models/sort-filter";
import type { ModelDocument } from "~/types/docs/model-document";
import CardUI from "~/components/ui/card";
import { pageConfig as config } from "~/config/_pages/models";
import Button from "~/components/ui/Button";
import Link from "next/link";
import { formatCreditsPerToken, getModelPath } from "~/lib/models";

const subscriptions = config.subscriptions;

const TAKE = 9;

interface ConsumeProps {
  initialData: ModelDocument[];
}

export default function Consume({ initialData }: ConsumeProps) {
  const [search, setSearch] = useState<string>("");
  const [skip, setSkip] = useState(0);

  const [sortBy, setSortBy] = useState<SortBy>("none");
  const [filterBy, setFilterBy] = useState<FilterBy>(
    "all",
  );

  const [accumulated, setAccumulated] = useState<ModelDocument[]>(initialData);
  const loaderRef = useRef<HTMLDivElement>(null);

  const isActive = !!search || sortBy !== "none" || filterBy !== "all";

  const { data: searchResult, isLoading } = api.models.search.useQuery(
    { query: search, skip, take: TAKE, sort: sortBy, filter: filterBy },
    { enabled: isActive },
  );

  useEffect(() => {
    setSkip(0);
    if (!isActive) {
      setAccumulated(initialData);
    }
  }, [search, sortBy, filterBy, isActive, initialData]);

  useEffect(() => {
    if (searchResult?.results && isActive) {
      setAccumulated((prev) =>
        skip === 0 ? searchResult.results : [...prev, ...searchResult.results],
      );
    }
  }, [searchResult, skip, isActive]);

  const loadMore = useCallback(() => {
    if (!isLoading && searchResult?.results.length === TAKE) {
      setSkip((prev) => prev + TAKE);
    }
  }, [isLoading, searchResult]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { threshold: 0.1 },
    );
    const currentLoader = loaderRef.current;
    if (currentLoader) observer.observe(currentLoader);
    return () => {
      if (currentLoader) observer.unobserve(currentLoader);
    };
  }, [loadMore]);

  const handleSearch = useCallback((term: string) => {
    setSearch(term);
  }, []);

  const itemsToShow = isActive ? accumulated : initialData;

  return (
    <main className="mx-auto mt-24 mb-36 min-h-128 max-w-6xl px-4">
      <GreeterUI>
        <GreeterUI.Sub text={config.suptitle} />
        <GreeterUI.Title text={config.title} />
      </GreeterUI>
      <div>
        <SearchBoxUI handleSearch={handleSearch} />

        <SortFilterUI
          sortBy={sortBy}
          setSortBy={setSortBy}
          filterBy={filterBy}
          setFilterBy={setFilterBy}
        />

        <div className="mt-8 flex flex-col items-stretch gap-5 md:grid md:grid-cols-2 xl:grid-cols-2">
          {itemsToShow.map((model) => (
            <ModelCard key={model.id} model={model} />
          ))}
        </div>

        {isActive && searchResult?.results.length === TAKE && (
          <div
            ref={loaderRef}
            className="flex h-12 items-center justify-center text-sm text-gray-500"
          >
            {isLoading
              ? "در حال بارگذاری..."
              : "برای مشاهدهٔ بیشتر اسکرول کنید"}
          </div>
        )}
      </div>
    </main>
  );
}

const ModelCard = ({ model }: { model: ModelDocument }) => {
  return (
    <CardUI variant="morph" className="flex flex-col md:flex-row-reverse">
      <div className="flex w-full items-center justify-center md:w-1/3 md:p-5">
        <CardUI.Thumbnail
          src={model.thumbnail ?? "/icons/contact.webp"}
          alt={model.name}
          icon
        />
      </div>
      <CardUI.Body className="flex flex-1 flex-col px-4 py-5 md:w-2/3">
        <CardUI.Title>
          <Link
            href={getModelPath(model)}
            className="inline truncate hover:text-cyan-400"
            title={model.name}
          >
            {model.name}
          </Link>
          <CardUI.Badge>
            {subscriptions[model.requiredSubscription]}
          </CardUI.Badge>
        </CardUI.Title>
        <CardUI.Paragraph dir="auto">{model.description}</CardUI.Paragraph>

        <div className="mt-auto mb-4 flex gap-2">
          <CardUI.Box
            titleClassName="text-md"
            badge="کسر اعتبار"
            title={formatCreditsPerToken(model.credits.inputCost)}
            sub={config.card.input_token_cost_label}
          />
          <CardUI.Box
            titleClassName="text-md"
            badge="کسر اعتبار"
            title={formatCreditsPerToken(model.credits.outputCost)}
            sub={config.card.output_token_cost_label}
          />
        </div>
        <Link
          href={getModelPath(model)}
          className="mb-3 text-sm text-sky-300 hover:text-sky-200"
        >
          {config.detail.more} ←
        </Link>
        <Button link={config.pay_link} variant="sky">
          {config.card.topup.pre}
          <span className="text-green-950/80">
            {` ${subscriptions[model.requiredSubscription]}`}
          </span>
        </Button>
      </CardUI.Body>
    </CardUI>
  );
};
